import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import GameCard from '@components/GameCard';
import CommentsWidget from '@components/CommentsWidget';
import { decodeHtml } from '@lib/seo';
import { type Game } from '@lib/content';
import { calculateCategoryCounts, getGameCategories } from '@lib/game-utils';
import categories from '@content/categories.json';
import { siteConfig, GAME_PLAYER, DOM_IDS } from '../config';
import { useMotionReveal } from '@hooks/useMotionReveal';

/**
 * Props for GameDetailView component
 */
interface GameDetailViewProps {
  /** Game data to display */
  game: Game;
  /** URL slug of the current game */
  slug: string;
  /** Whether the game iframe has been initialized */
  gameStarted: boolean;
  /** Whether the game iframe is currently loading */
  isLoading: boolean;
  /** Whether to show "Back to Game" button in sidebar */
  showBackToGame: boolean;
  /** Array of games to show in sidebar */
  sidebarGames: Game[];
  /** Array of recommended/similar games */
  moreGames: Game[];
  /** Ref to the game iframe element */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Ref to the game player container */
  gamePlayerRef: React.RefObject<HTMLDivElement | null>;
  /** Callback when play button is clicked */
  onPlayClick: () => void;
  /** Callback when iframe finishes loading */
  onIframeLoad: () => void;
  /** Callback to scroll to game player */
  onScrollToGame: () => void;
  /** Whether this is being displayed on the homepage */
  isHomePage?: boolean;
  /** Whether theater mode is active */
  isTheaterMode?: boolean;
  /** Callback when theater mode button is clicked */
  onTheaterModeToggle?: () => void;
  /** Whether to show the cover overlay */
  showCoverOverlay?: boolean;
  /** Whether fullscreen has been initialized */
  hasInitializedFullscreen?: boolean;
  /** Callback when overlay play button is clicked */
  onOverlayPlay?: () => void;
  /** Whether to show black screen */
  showBlackScreen?: boolean;
}

/**
 * GameDetailView Component
 * 
 * Main game detail page layout displaying:
 * - Game player with splash screen and iframe
 * - Game information (title, description, categories)
 * - Action buttons (like, dislike, report, comments, fullscreen)
 * - Sidebar with related games
 * - Recommended games section
 * - Comments widget
 * 
 * Uses memoization for performance optimization of category calculations.
 * Handles fullscreen API across different browsers (standard, webkit, ms).
 * 
 * @component
 */
const GameDetailView: React.FC<GameDetailViewProps> = ({
  game,
  slug,
  gameStarted,
  isLoading,
  showBackToGame,
  sidebarGames,
  moreGames,
  iframeRef,
  gamePlayerRef,
  onPlayClick,
  onIframeLoad,
  onScrollToGame,
  isHomePage = false,
  isTheaterMode = false,
  onTheaterModeToggle,
  showCoverOverlay = false,
  hasInitializedFullscreen = false,
  onOverlayPlay,
  showBlackScreen = false,
}) => {
  // Get primary category for breadcrumb
  const primaryCategory = game.categories?.[0];
  const currentCategory = primaryCategory ? categories.find(c => c.slug === primaryCategory) : null;

  // Reaction state (like/dislike)
  const [reaction, setReaction] = useState<'like' | 'dislike' | null>(null);
  const [shareMessage, setShareMessage] = useState<string>('');
  const motionRootRef = useRef<HTMLDivElement>(null);
  const hasHowToPlayInBody = Boolean(
    game.fullDescription && /how to play/i.test(game.fullDescription)
  );
  const ratingValue = Number.parseFloat(String(game.rating || '4.5')) || 4.5;

  useMotionReveal(motionRootRef, slug);

  // Load reaction from localStorage on mount
  useEffect(() => {
    const storageKey = `game-reaction-${slug}`;
    const savedReaction = localStorage.getItem(storageKey);
    if (savedReaction === 'like' || savedReaction === 'dislike') {
      setReaction(savedReaction);
    }
  }, [slug]);

  const handleReaction = (type: 'like' | 'dislike') => {
    const storageKey = `game-reaction-${slug}`;

    if (reaction === type) {
      // Unset if clicking the same button
      setReaction(null);
      localStorage.removeItem(storageKey);
    } else {
      // Set new reaction
      setReaction(type);
      localStorage.setItem(storageKey, type);
    }
  };

  const handleShare = async (evt: React.MouseEvent) => {
    evt.preventDefault();

    const shareData = {
      title: game.title,
      text: `Play ${game.title} - ${game.description}`,
      url: window.location.href,
    };

    try {
      // Try Web Share API first (mobile-friendly)
      if (navigator.share) {
        await navigator.share(shareData);
        setShareMessage('✓ Shared!');
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        setShareMessage('✓ Link copied!');
      }

      // Clear message after 2 seconds
      setTimeout(() => setShareMessage(''), 2000);
    } catch (error) {
      // User cancelled or error occurred
      if (error instanceof Error && error.name !== 'AbortError') {
        setShareMessage('✗ Failed to share');
        setTimeout(() => setShareMessage(''), 2000);
      }
    }
  };

  // Get all game categories for tags (memoized for performance)
  const gameCategories = useMemo(() => getGameCategories(game), [game]);

  // Calculate actual game counts for each category (memoized for performance)
  const categoryCounts = useMemo(
    () => calculateCategoryCounts(gameCategories),
    [gameCategories]
  );

  const scrollToComments = (evt: React.MouseEvent) => {
    evt.preventDefault();
    const commentsSection = document.getElementById(DOM_IDS.COMMENTS_WIDGET);
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openFullscreen = () => {
    const node = iframeRef.current;
    if (!node) return;
    const el = node as HTMLElement & {
      webkitRequestFullscreen?: () => void;
      msRequestFullscreen?: () => void;
    };
    if (node.requestFullscreen) {
      node.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  };

  const reloadGame = () => {
    const node = iframeRef.current;
    if (!node) return;
    const currentSrc = node.src;
    node.src = '';
    setTimeout(() => {
      node.src = currentSrc;
    }, 100);
  };


  const isBadParentingHome = isHomePage && game.slug === 'bad-parenting';

  return (
    <>
      <div className="page-body game-detail-motion" ref={motionRootRef}>
        <div className="container">
          <div className="game-container">
            <div className="row">
              <div className="col-12 col-lg-9 game-content">
                <div className={`game-player ${isTheaterMode ? 'theater-mode' : ''}`} ref={gamePlayerRef}>
                  <div
                    className={`game-iframe-container ${showBlackScreen ? 'black-screen-active show-black-screen' : ''}`}
                    style={{ paddingTop: GAME_PLAYER.DEFAULT_ASPECT_RATIO }}
                  >
                    {!gameStarted && (
                      <div className="game-splash-screen" id="game-arena">
                        <img src={game.thumbnail} alt={`Play ${game.title} - Free online game`} className="splash-background" width="800" height="600" />
                        <div className="splash-overlay">
                          <img src={game.thumbnail} alt={`${game.title} game thumbnail`} className="splash-icon" width="120" height="120" />
                          <button className="play-now-btn" onClick={onPlayClick} aria-label="Play game">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <title>Play icon</title>
                              <path d="M8 5v14l11-7z" />
                            </svg>
                            {GAME_PLAYER.PLAY_BUTTON_TEXT}
                          </button>
                        </div>
                      </div>
                    )}
                    {gameStarted && (
                      <>
                        {isLoading && (
                          <div className="game-loading">
                            <div className="loading-spinner"></div>
                            <p>{GAME_PLAYER.LOADING_TEXT}</p>
                          </div>
                        )}
                        <iframe
                          ref={iframeRef}
                          className="game-iframe"
                          id="gameFrame"
                          src={game.playUrl}
                          style={{ background: 'black', opacity: isLoading ? 0 : 1 }}
                          width={GAME_PLAYER.DEFAULT_WIDTH}
                          height={GAME_PLAYER.DEFAULT_HEIGHT}
                          scrolling="no"
                          frameBorder={0}
                          title={game.title}
                          allowFullScreen
                          allow="autoplay; fullscreen; gamepad; payment; clipboard-write; camera; microphone"
                          referrerPolicy="no-referrer"
                          onLoad={onIframeLoad}
                        />
                        {isBadParentingHome && showCoverOverlay && (
                          <div
                            className="game-menu-overlay"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onOverlayPlay?.();
                            }}
                            role="button"
                            aria-label="Click to enter theater mode"
                            style={{ cursor: 'pointer' }}
                          >
                            <img src="/assets/img/bg-cover.png" alt="Game Menu" className="menu-background" width="400" height="300" />
                          </div>
                        )}
                        {showBlackScreen && (
                          <div className="fullscreen-black-overlay" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#000',
                            zIndex: 9999
                          }} />
                        )}
                      </>
                    )}
                  </div>
                  <div className="single-info-container">
                    <div className="header-left">
                      <img width={40} height={40} src={game.thumbnail} alt={`${game.title} icon`} />
                      <div className="single-title">{game.title}</div>
                    </div>
                    <div className="action-btn">
                      <div className="stats-vote">
                        <a className={`icon-btn ${reaction === 'like' ? 'liked' : ''}`} href="#like" onClick={(evt) => { evt.preventDefault(); handleReaction('like'); }} aria-label="Like this game">
                          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <title>Like</title>
                            <path d="M2 10.5C2 9.67157 2.67157 9 3.5 9C4.32843 9 5 9.67157 5 10.5V16.5C5 17.3284 4.32843 18 3.5 18C2.67157 18 2 17.3284 2 16.5V10.5Z" fill="currentColor"></path>
                            <path d="M6 10.3333V15.7639C6 16.5215 6.428 17.214 7.10557 17.5528L7.15542 17.5777C7.71084 17.8554 8.32329 18 8.94427 18H14.3604C15.3138 18 16.1346 17.3271 16.3216 16.3922L17.5216 10.3922C17.7691 9.15465 16.8225 8 15.5604 8H12V4C12 2.89543 11.1046 2 10 2C9.44772 2 9 2.44772 9 3V3.66667C9 4.53215 8.71929 5.37428 8.2 6.06667L6.8 7.93333C6.28071 8.62572 6 9.46785 6 10.3333Z" fill="currentColor"></path>
                          </svg>
                        </a>
                        <a className={`icon-btn ${reaction === 'dislike' ? 'disliked' : ''}`} href="#dislike" onClick={(evt) => { evt.preventDefault(); handleReaction('dislike'); }} aria-label="Dislike this game">
                          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <title>Dislike</title>
                            <path d="M18 9.5C18 10.3284 17.3285 11 16.5 11C15.6716 11 15 10.3284 15 9.5V3.5C15 2.67157 15.6716 2 16.5 2C17.3285 2 18 2.67157 18 3.5V9.5Z" fill="currentColor"></path>
                            <path d="M14 9.66667V4.23607C14 3.47852 13.572 2.786 12.8945 2.44721L12.8446 2.42229C12.2892 2.14458 11.6767 2 11.0558 2L5.63964 2C4.68628 2 3.86545 2.67292 3.67848 3.60777L2.47848 9.60777C2.23097 10.8453 3.17755 12 4.43964 12H8.00004V16C8.00004 17.1046 8.89547 18 10 18C10.5523 18 11 17.5523 11 17V16.3333C11 15.4679 11.2807 14.6257 11.8 13.9333L13.2 12.0667C13.7193 11.3743 14 10.5321 14 9.66667Z" fill="currentColor"></path>
                          </svg>
                        </a>
                        <a className="icon-btn reload-btn" href="#reload" onClick={(evt) => { evt.preventDefault(); reloadGame(); }} aria-label="Reload game">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                            <title>Reload game</title>
                            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                          </svg>
                        </a>
                        <a className="icon-btn" href="#comments" onClick={scrollToComments} aria-label="View comments">
                          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <title>Comments</title>
                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                          </svg>
                        </a>
                        <a className="icon-btn" href="#theater" onClick={(evt) => { evt.preventDefault(); onTheaterModeToggle?.(); }} aria-label={isTheaterMode ? "Exit theater mode" : "Enter theater mode"}>
                          {isTheaterMode ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <title>Exit Theater Mode</title>
                              <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" fill="currentColor" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <title>Theater Mode</title>
                              <path d="M19 6H5c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H5V8h14v8z" fill="currentColor" />
                            </svg>
                          )}
                        </a>
                        <a className="icon-btn" href="#fullscreen" onClick={(evt) => { evt.preventDefault(); openFullscreen(); }} aria-label="Toggle fullscreen">
                          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="-0.5 0 20 20" fill="none" aria-hidden="true">
                            <title>Fullscreen</title>
                            <path d="M3 8V4M3 4H7M3 4L7 8M15 8V4M15 4H11M15 4L11 8M3 12V16M3 16H7M3 16L7 12M15 16L11 12M15 16V12M15 16H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-3">
                <div className="sidebar">
                  <div className="widget">
                    <div className="row">
                      {sidebarGames.map((item) => (
                        <div key={item.slug} className="col-3 col-sm-2 col-lg-4 p-0 item-right">
                          <Link to={siteConfig.siteMode === 'single-game' && item.slug === siteConfig.gameSlug ? '/' : `/${item.slug}`}>
                            <div className="list-game">
                              <div className="list-thumbnail square-thumb">
                                <img src={item.thumbnail} alt={`Play ${item.title} online`} loading="lazy" />
                                <div className="hover-info"><span>{item.title}</span></div>
                              </div>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Game Meta Section */}
            <div className="game-meta">
              {/* Breadcrumb */}
              <div className="game-breadcrumb">
                <Link to="/">Games</Link>
                <span className="separator">›</span>
                {currentCategory && !isHomePage && (
                  <>
                    <Link to={`/category/${currentCategory.slug}`}>{currentCategory.name}</Link>
                    <span className="separator">›</span>
                  </>
                )}
                {isHomePage && currentCategory && (
                  <>
                    <Link to={`/category/${currentCategory.slug}`}>{currentCategory.name}</Link>
                    <span className="separator">›</span>
                  </>
                )}
                <span>{game.title}</span>
              </div>

              <div className="game-meta-header">
                <h1 className="game-meta-title">{game.title}</h1>
                <div className="game-share-wrapper">
                  <a href="#share" className="game-share-btn" onClick={handleShare}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
                    </svg>
                    Share
                  </a>
                  {shareMessage && <span className="share-feedback">{shareMessage}</span>}
                </div>
              </div>

              <p className="game-short-description">
                {game.shortIntro || game.description}
              </p>

              <div className="info-card-grid">
                <div className="info-card">
                  <span className="info-card-icon google-g" aria-hidden="true">G</span>
                  <span className="info-card-label">Developer</span>
                  <span className="info-card-value">{game.developer || 'Unknown Developer'}</span>
                </div>
                <div className="info-card">
                  <span className="info-card-icon rating-stars" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const filled = ratingValue >= star;
                      const half = !filled && ratingValue >= star - 0.5;
                      return (
                        <span
                          key={star}
                          className={`rating-star${filled ? ' filled' : half ? ' half' : ''}`}
                        >
                          ★
                        </span>
                      );
                    })}
                  </span>
                  <span className="info-card-label">Rating</span>
                  <span className="info-card-value">
                    {game.rating || '5.0'} ({game.votes || '1,234'} votes)
                  </span>
                </div>
                <div className="info-card">
                  <span className="info-card-icon calendar-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="3" y="5" width="18" height="16" rx="2" />
                      <path d="M3 10h18M8 3v4M16 3v4" />
                    </svg>
                  </span>
                  <span className="info-card-label">Released</span>
                  <span className="info-card-value">{game.released || '2024'}</span>
                </div>
                <div className="info-card">
                  <span className="info-card-icon tech-icon" aria-hidden="true">{'</>'}</span>
                  <span className="info-card-label">Technology</span>
                  <span className="info-card-value">{game.technology || 'HTML5'}</span>
                </div>
              </div>

              <div className="game-meta-categories">
                {gameCategories.map((cat) => cat && (
                  <Link key={cat.slug} className="game-meta-category" to={`/category/${cat.slug}`}>
                    {cat.name}
                    <span className="count">{categoryCounts[cat.slug] || 0}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Description Section */}
            <div className="info-the-game">
              <h2 className="content-heading section-fade-in">
                <span className="heading-emoji">🏆</span> About {game.title}
              </h2>
              <div className="single-description">
                {game.fullDescription ? (
                  <div dangerouslySetInnerHTML={{ __html: decodeHtml(game.fullDescription) }} />
                ) : (
                  <p>{game.description}</p>
                )}
              </div>

              {/* Game Story Section */}
              {game.gameStory && (
                <>
                  <h2 style={{ marginTop: '2rem' }}>The Story Behind {game.title}</h2>
                  <div className="single-description">
                    <p style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}>{game.gameStory}</p>
                  </div>
                </>
              )}

              {!hasHowToPlayInBody && (
                <>
                  <h2 style={{ marginTop: '2rem' }}>How to Play {game.title}</h2>
                  <div className="single-description">
                    <p style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}>{game.howToPlay || 'Click or tap to play. Follow the on-screen prompts.'}</p>
                  </div>
                </>
              )}
            </div>

            {/* Tips & Tricks Section */}
            {game.gameTips && game.gameTips.length > 0 && (
              <div className="info-the-game" style={{ marginTop: '2rem' }}>
                <h2>Expert Tips & Strategies</h2>
                <div className="tips-container">
                  {game.gameTips.map((tip, index) => (
                    <div key={index} className="tip-item" style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        💡 {tip.title}
                      </h3>
                      <p style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                        {tip.tip}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ Section */}
            {game.faqs && game.faqs.length > 0 && (
              <div className="info-the-game section-fade-in" style={{ marginTop: '2rem' }}>
                <h2 className="content-heading section-fade-in">
                  <span className="heading-emoji">❓</span> {game.title} FAQ
                </h2>
                <div className="faq-accordion">
                  {game.faqs.map((faq, index) => (
                    <details key={index} className="faq-item">
                      <summary>{faq.question}</summary>
                      <p>{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* More Games Like This Section */}
            <h2 className="text-center section-title" style={{ marginTop: '16px' }}>More Games Like This</h2>
            <div className="row">
              {moreGames.map((item) => (
                <div key={item.slug} className="col-6 col-sm-6 col-md-4 col-lg-3 col-xl-2">
                  <GameCard game={item} mainGameSlug={siteConfig.gameSlug} />
                </div>
              ))}
            </div>

            {/* Comments Section */}
            <div className="comments-container">
              <CommentsWidget slug={slug} />
            </div>
          </div>
        </div>
      </div>

      {/* Back to Game Button */}
      <button
        className={`back-to-game-btn ${showBackToGame ? 'visible' : ''}`}
        onClick={onScrollToGame}
        aria-label="Back to game"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
        </svg>
        Back to game
      </button>
    </>
  );
};

export default GameDetailView;
